import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem("em_token");
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (_) {
            localStorage.removeItem("em_token");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password, totpCode) => {
        try {
            const body = { email, password };
            if (totpCode) body.totp_code = totpCode;
            const { data } = await api.post("/auth/login", body);
            localStorage.setItem("em_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            const detail = e.response?.data?.detail;
            // Le backend signale "2FA requise" via un detail objet { code: "2fa_required" }
            if (detail && typeof detail === "object" && detail.code === "2fa_required") {
                return { ok: false, twofaRequired: true, error: "" };
            }
            return { ok: false, error: formatApiErrorDetail(detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            localStorage.setItem("em_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = () => {
        localStorage.removeItem("em_token");
        setUser(null);
    };

    // Permissions RBAC de l'admin courant (cf. rbac_spec.md §1/§3). `null`/absent
    // = compat legacy : l'admin voit tout, exactement comme avant l'introduction
    // du RBAC (aucune migration de données n'est requise côté backend).
    const permissions = user?.permissions ?? null;

    const hasPerm = useCallback(
        (perm) => {
            if (!perm) return true; // pas de garde sur ce module/action (ex. "Mon compte")
            if (!user) return false;
            if (user.role !== "admin") return true; // RBAC ne s'applique qu'aux admins
            if (!user.permissions) return true; // legacy : pas de champ permissions → tout autorisé
            return user.permissions.includes(perm);
        },
        [user]
    );

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, permissions, hasPerm }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
